import { useReducer } from "react";

interface WizardState {
  step: number;
  visited: number[];
}

type WizardAction =
  { type: "next" } | { type: "back" } | { type: "goto"; step: number } | { type: "reset" };

function wizardReducer(state: WizardState, action: WizardAction, lastStep: number): WizardState {
  switch (action.type) {
    case "next": {
      const step = Math.min(state.step + 1, lastStep);
      return { step, visited: [...new Set([...state.visited, step])] };
    }
    case "back":
      return { ...state, step: Math.max(state.step - 1, 0) };
    case "goto":
      return { step: action.step, visited: [...new Set([...state.visited, action.step])] };
    case "reset":
      return { step: 0, visited: [0] };
  }
}

/**
 * Reference pattern for local multi-step state (e.g. a tax filing wizard)
 * that's too involved for plain useState. Use useReducer + a typed action
 * union like this instead of several boolean/number useState calls.
 * Cross-page/global state belongs in a Zustand store (see features/auth/store).
 */
export function useStepWizard(totalSteps: number) {
  const lastStep = totalSteps - 1;
  const [state, dispatch] = useReducer(
    (s: WizardState, a: WizardAction) => wizardReducer(s, a, lastStep),
    { step: 0, visited: [0] },
  );

  return {
    step: state.step,
    visited: state.visited,
    isFirst: state.step === 0,
    isLast: state.step === lastStep,
    next: () => dispatch({ type: "next" }),
    back: () => dispatch({ type: "back" }),
    goto: (step: number) => dispatch({ type: "goto", step }),
    reset: () => dispatch({ type: "reset" }),
  };
}
